// src/utils/authProfile.ts
//
// Jméno a členské číslo přihlášeného pro účet v hlavičce (ui/AccountMenu).
// Drží je AuthProvider (useAuth) – bere je ze stejného čtení users/{uid}
// jako roli, hlavička tak nepřidává žádné čtení.
//
// Profil ale vzniká až dokončením registrace při prvním přihlášení
// (utils/completeRegistration) – AuthProvider ho v tu chvíli obvykle ještě
// nenajde – a jméno jde změnit v Úpravě profilu. Obrazovky, které profil
// čtou samy (useCurrentUser: Přihlášky, Výsledky, Profil…), proto čerstvě
// přečtená data předají AuthProvideru (ProfileSyncContext): hlavička se
// obnoví bez dalšího čtení i bez nového načtení stránky.
//
// Samostatný modul, ne useAuth.tsx: testy, které useAuth nahrazují mockem,
// tak useCurrentUser nerozbijí.

import { createContext, useContext } from "react";
import type { DocumentData } from "firebase/firestore";
import { isValidSequenceNumber } from "./memberNumber";

export interface AuthProfile {
  /** Jméno a příjmení bez okrajových mezer; "" = neuvedeno. */
  firstName: string;
  lastName: string;
  /**
   * Platné členské číslo tak, jak je uložené ("042" – stejně jako v Moje
   * údaje a ve variabilním symbolu), jinak null.
   */
  memberNumber: string | null;
}

/**
 * Stav profilu: "loaded" – údaje jsou v AuthProfile; "missing" – dokument
 * users/{uid} neexistuje (registrace se dokončí až prvním přihlášením);
 * "unknown" – ještě se nenačetl, čtení selhalo (offline) nebo nikdo není
 * přihlášený.
 */
export type ProfileStatus = "unknown" | "loaded" | "missing";

const text = (raw: unknown): string =>
  typeof raw === "string" ? raw.trim() : "";

/** Údaje pro hlavičku z dat users/{uid}. */
export function profileFromDoc(data: DocumentData): AuthProfile {
  const raw: unknown = data.sequenceNumber;
  return {
    firstName: text(data.firstName),
    lastName: text(data.lastName),
    memberNumber: isValidSequenceNumber(raw) ? String(raw).trim() : null,
  };
}

/** Stejné údaje – obnova se stejnými daty konzumenty useAuth nepřekreslí. */
export function sameProfile(
  a: AuthProfile | null,
  b: AuthProfile | null
): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.memberNumber === b.memberNumber
  );
}

/**
 * Předá AuthProvideru čerstvě přečtený users/{uid}: data, nebo null =
 * dokument neexistuje. Údaje jiného než přihlášeného uživatele se zahodí,
 * roli obnova nemění (ta se čte jen při změně přihlášení).
 */
export type ProfileSync = (uid: string, data: DocumentData | null) => void;

const noopSync: ProfileSync = () => undefined;

/** Mimo AuthProvider (testy obrazovek) obnova nic nedělá. */
export const ProfileSyncContext = createContext<ProfileSync>(noopSync);

export function useProfileSync(): ProfileSync {
  return useContext(ProfileSyncContext);
}
